import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { ClerkProvider } from '@clerk/clerk-react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider, createBrowserRouter } from 'react-router-dom';
import App from './App';
import { SignInPage } from './pages/SignInPage';
import { AppLayout } from './layouts/AppLayout';
import { HomeDashboardPage } from './pages/HomeDashboardPage';
import { AgentDetailPage } from './pages/AgentDetailPage';
import { VoiceProfilePage } from './pages/VoiceProfilePage';
import { ClientLayout } from './layouts/ClientLayout';
import { DashboardPage } from './pages/DashboardPage';
import { ApprovalsPage } from './pages/ApprovalsPage';
import { ContentPage } from './pages/ContentPage';
import { AgentsPage } from './pages/AgentsPage';
import { CalendarPage } from './pages/CalendarPage';
import { FinancePage } from './pages/FinancePage';
import { SystemPage } from './pages/SystemPage';
import { SettingsPage } from './pages/SettingsPage';
import { PromptEditPage } from './pages/PromptEditPage';
import { AdminClientsPage } from './pages/AdminClientsPage';
import { AdminOnboardingPage } from './pages/AdminOnboardingPage';
import { ClientSetupPage } from './pages/ClientSetupPage';
import { NotFoundPage } from './pages/NotFoundPage';
import { SetupRequiredPage } from './pages/SetupRequiredPage';
import { IS_DEMO_MODE } from './lib/auth';
import './index.css';

// === Détection des variables manquantes ===
const PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

const missingVars: string[] = [];
if (!IS_DEMO_MODE) {
  if (!PUBLISHABLE_KEY || PUBLISHABLE_KEY === 'pk_test_xxx') missingVars.push('VITE_CLERK_PUBLISHABLE_KEY');
}
const useMocks = import.meta.env.VITE_USE_MOCKS === 'true';
if (!useMocks && !IS_DEMO_MODE) {
  if (!SUPABASE_URL) missingVars.push('VITE_SUPABASE_URL');
  if (!SUPABASE_ANON_KEY) missingVars.push('VITE_SUPABASE_ANON_KEY');
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      retry: 2,
      refetchOnWindowFocus: false,
    },
  },
});

const router = createBrowserRouter([
  {
    path: '/',
    element: <App />,
    children: [
      // Auth
      { path: 'sign-in', element: <SignInPage /> },
      { path: 'sign-in/*', element: <SignInPage /> },

      // Espace client (post-login). Layout résout le tenant via email Clerk.
      {
        element: <AppLayout />,
        children: [
          { index: true, element: <HomeDashboardPage /> },
          { path: 'agents/:id', element: <AgentDetailPage /> },
          { path: 'voice-profile', element: <VoiceProfilePage /> },
        ],
      },

      // === LEGACY admin / multi-tenant (Sebastian uniquement). ===
      // Conservé pour outils internes : approvals, content, system, settings,
      // prompt registry. Pas exposé dans le menu coach.
      { path: 'clients/:slug/setup', element: <ClientSetupPage /> },
      {
        path: 'clients/:slug',
        element: <ClientLayout />,
        children: [
          { index: true, element: <DashboardPage /> },
          { path: 'approvals', element: <ApprovalsPage /> },
          { path: 'content', element: <ContentPage /> },
          { path: 'calendar', element: <CalendarPage /> },
          { path: 'agents', element: <AgentsPage /> },
          { path: 'finance', element: <FinancePage /> },
          { path: 'system', element: <SystemPage /> },
          { path: 'settings', element: <SettingsPage /> },
          { path: 'settings/prompts/:name', element: <PromptEditPage /> },
        ],
      },
      { path: 'admin/clients', element: <AdminClientsPage /> },
      { path: 'admin/clients/new', element: <AdminOnboardingPage /> },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
]);

function Root() {
  if (IS_DEMO_MODE) {
    return (
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
      </QueryClientProvider>
    );
  }
  return (
    <ClerkProvider publishableKey={PUBLISHABLE_KEY!} afterSignOutUrl="/sign-in">
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
      </QueryClientProvider>
    </ClerkProvider>
  );
}

if (missingVars.length > 0) {
  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <SetupRequiredPage missingVars={missingVars} />
    </StrictMode>
  );
} else {
  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <Root />
    </StrictMode>
  );
}
